import {useEffect, useCallback, useContext} from 'preact/hooks'
import {
  drfFetch,
  TextInput,
  LoadingEllipsis,
  useFormField,
  useStore,
  useUser,
  toTitleCase,
  classNames
} from '@/core'
import {Card} from '@c/card'
import {useAdvancedSkills} from '@u/drf/hooks'
import styles from './style.module.styl'

const SkillPill = ({skill, selected, ...props}) =>
  <button type='button' className={classNames(styles.skillPill, selected && styles.selected)} {...props}>
    {skill.name}
  </button>

const SearchPanel = ({onChooseSkill, newSkill, onChooseNewSkill, results}) =>
  <Card className={styles.searchPanel} noPadding >
    {results?.map(skill =>
      <button onClick={() => {onChooseSkill(skill)}} >{skill.name}</button>
    )}
    {results && newSkill && newSkill.length > 1 && !results.find(s => s.name === newSkill) &&
      <button onClick={() => onChooseNewSkill(newSkill)} >{newSkill}</button>
    }
  </Card>

export const SkillSelect = ({name}) => {
  const store = useStore()
  const user = useUser()

  const {value = [], setValue, error, formName} = useFormField(name)

  // Since defaultValue is just ids, we need the names as well
  const [defaultValueSkills] = useAdvancedSkills({queries: {ids: value}, dependencies: [name, formName]})

  // the search value
  const [search, setSearch] = store.use(['skill-select', name, 'search'], '', {setByFunction: true})

  // the search results in the dropdown panel
  const [searchResults] = useAdvancedSkills({queries: {search, limit: 10}})

  // the initial pills
  const [recommendedFromApi] = useAdvancedSkills({queries: {is_recommended: true}})

  // any skills that are selected in the search panel also get added to the pills
  const [recommendedAdditional, setRecommendedAdditional] = store.use(['skill-select', name, 'recommended'], [], {setByFunction: true, cleanup: true})

  const recommended = [...(recommendedFromApi || []), ...recommendedAdditional]
  const [selected, setSelected] = store.use(['skill-select', name, 'selected'], [], {setByFunction: true})
  const [showSearchPanel, setShowSearchPanel] = store.use(['skill-select', name, 'search-panel'], false, {setByFunction: true})
  const [isEditMode, setIsEditMode] = store.use(['skill-select', name, 'edit-mode'], false)
  const onChooseSkill= (skill) => {
    setSearch('')
    setRecommendedAdditional(current => current.find(x => x.id === skill.id) ? current : [...current, skill], {noPublish: true})
    setSelected(current => current.find(x => x.id === skill.id) ? current : [...current, skill])
  }
  const onChooseNewSkill = (name) => {
    drfFetch('advancedSkills', {method: 'POST', body: {name}, token: user?.token})
      .then(res => {
        setSelected(current => current.map(s => s.name === res.name ? res : s))
        setValue(current => current.map(s => s === res.name ? res.id : s))
        setRecommendedAdditional(current => current.map(s => s.name === res.name ? res : s))
      })
    onChooseSkill({name, id: name})
  }

  useEffect(() => {
    const listener = (ev) => {
      if (ev.target.closest('input[name="search"]'))
        return
      setShowSearchPanel(false)
    }
    window.addEventListener('click', listener)
    return () => window.removeEventListener('click', listener)
  }, [])

  useEffect(() => {
    defaultValueSkills && setSelected(defaultValueSkills)
  }, [!defaultValueSkills])

  useEffect(() => {
    setValue(selected.map(s => s.id))
  }, [selected.length])

  return (
    <div className={styles.skillSelect} >
      {!defaultValueSkills && <LoadingEllipsis />}
      {defaultValueSkills &&
        <>
          <div className={styles.search}>
            <TextInput
              autocomplete='off'
              compact
              name='search'
              placeholder='Search'
              value={search}
              onFocus={() => {
                setShowSearchPanel(true)
              }}
              onChange={ev => {
                setShowSearchPanel(ev.target.value.length > 0)
                setSearch(ev.target.value)
                return true
              }}
            />
            {showSearchPanel && <SearchPanel
              newSkill={toTitleCase(search, true)}
              onChooseNewSkill={onChooseNewSkill}
              results={searchResults}
              onChooseSkill={(skill) => {
                setSearch('')
                setRecommendedAdditional(current => (current.find(x => x.id === skill.id) || recommendedFromApi.find(x => x.id === skill.id)) ? current : [...current, skill], {noPublish: true})
                setSelected(current => current.find(x => x.id === skill.id) ? current : [...current, skill])
              }}
            />}
          </div>
          <div className={styles.popular} >
            {!recommended && <LoadingEllipsis />}
            {recommended &&
              <div className={styles.skillPills} >
                {recommended.map((skill) =>
                  <SkillPill skill={skill} selected={selected.find(x => x.id === skill.id)} onClick={() => setSelected(curr => curr.find(x => x.id === skill.id) ? curr.filter(x => x.id !== skill.id) : [...curr, skill])} />
                )}
              </div>
            }
          </div>
          {selected.length > 0 &&
            <div className={styles.mySkills} >
              <div className={styles.mySkillsHeader} >
                <h2>My Skills</h2>
                <button type='button' onClick={() => setIsEditMode(!isEditMode)} className={styles.textButton} >
                  {isEditMode ? 'Done' : 'Edit'}
                </button>
              </div>
              <div>
                {selected.map(skill =>
                  <div>
                    {skill.name} {isEditMode && <button type='button' className={styles.textButton} onClick={() => setSelected(curr => curr.filter(x => x.id !== skill.id))} >Remove</button>}
                  </div>
                )}
              </div>
            </div>
          }
        </>
      }
    </div>
  )
}
